/**
 * PhaseIndicator Component
 *
 * Displays the current game phase with appropriate styling and messaging.
 * Phases: setup, waiting, playing, ended
 *
 * Props:
 * - phase: Current game phase (setup, waiting, playing, ended)
 * - playerId: ID of the current player
 * - winner: (optional) ID of the winning player (used in ended phase)
 */

import { COPY } from '../copy';

export default function PhaseIndicator({ phase, playerId, winner }) {
  // Phase configuration
  const phaseConfig = {
    setup: {
      title: COPY.phase.setup.title,
      message: COPY.phase.setup.message,
      bgColor: 'bg-dark-purple',
      borderColor: 'border-neon-cyan',
      shadowColor: 'shadow-glow-cyan',
      textColor: 'text-neon-cyan',
      icon: COPY.phase.setup.emoji,
    },
    waiting: {
      title: COPY.phase.waiting.title,
      message: COPY.phase.waiting.message,
      bgColor: 'bg-dark-purple',
      borderColor: 'border-neon-yellow',
      shadowColor: 'shadow-glow-yellow',
      textColor: 'text-neon-yellow',
      icon: COPY.phase.waiting.emoji,
    },
    playing: {
      title: COPY.phase.playing.title,
      message: COPY.phase.playing.message,
      bgColor: 'bg-dark-purple',
      borderColor: 'border-neon-green',
      shadowColor: 'shadow-glow-green',
      textColor: 'text-neon-green',
      icon: COPY.phase.playing.emoji,
    },
    ended: {
      title: COPY.phase.gameOver.titleLose,
      message: null, // Will be determined based on winner
      bgColor: 'bg-dark-purple',
      borderColor: null, // Will be determined based on winner
      shadowColor: null, // Will be determined based on winner
      textColor: null, // Will be determined based on winner
      icon: null, // Will be determined based on winner
    },
  };

  // Handle invalid or null phase
  if (!phase || !phaseConfig[phase]) {
    return (
      <div
        data-testid="phase-indicator"
        aria-live="polite"
        aria-label="Game phase indicator"
        role="status"
        className="text-center p-4 rounded-lg bg-dark-purple border-2 border-gray-600 text-gray-400"
      >
        <p className="text-lg font-semibold">Unknown Phase</p>
      </div>
    );
  }

  // Get configuration for current phase
  let config = { ...phaseConfig[phase] };

  // Special handling for ended phase
  if (phase === 'ended') {
    const playerWon = winner === playerId;

    if (winner) {
      config.title = playerWon ? COPY.phase.gameOver.titleWin : COPY.phase.gameOver.titleLose;
      config.message = playerWon ? COPY.phase.gameOver.messageWin : COPY.phase.gameOver.messageLose;
      config.borderColor = playerWon ? 'border-neon-green' : 'border-neon-pink';
      config.shadowColor = playerWon ? 'shadow-glow-green' : 'shadow-glow-pink';
      config.textColor = playerWon ? 'text-neon-green' : 'text-neon-pink';
      config.icon = playerWon ? COPY.phase.gameOver.emojiWin : COPY.phase.gameOver.emojiLose;
    } else {
      config.message = 'Game Ended';
      config.borderColor = 'border-gray-600';
      config.shadowColor = '';
      config.textColor = 'text-gray-400';
      config.icon = '🏁';
    }
  }

  const ariaLabel = `Game phase: ${config.title}. ${config.message}`;

  return (
    <div
      data-testid="phase-indicator"
      aria-live="polite"
      aria-label={ariaLabel}
      role="status"
      className={`text-center p-4 rounded-lg border-2 ${config.bgColor} ${config.borderColor} ${config.shadowColor} ${config.textColor} transition-all duration-300`}
    >
      <div className="flex items-center justify-center gap-2">
        {config.icon && <span className="text-2xl">{config.icon}</span>}
        <div>
          <p className="text-lg font-bold">{config.title}</p>
          <p className="text-sm text-gray-300">{config.message}</p>
        </div>
      </div>
    </div>
  );
}
